<!-- Product Detail Page - View Only -->
<div class="product-detail-page">
  <!-- Loading Skeleton -->
  @if (isLoading()) {
    <div class="loading-container">
      <div class="header-skeleton">
        <p-skeleton width="32px" height="32px" styleClass="border-circle" />
        <p-skeleton width="200px" height="24px" />
        <p-skeleton width="150px" height="20px" />
      </div>
      <div class="content-skeleton">
        <div class="panel-skeleton">
          <p-skeleton width="100%" height="100%" />
        </div>
        <div class="details-skeleton">
          <p-skeleton width="100%" height="100%" />
        </div>
      </div>
    </div>
  }

  <!-- Main Content -->
  @if (!isLoading() && product()) {
    <!-- Slim Header -->
    <div class="page-header">
      <div class="header-left">
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          [rounded]="true"
          severity="secondary"
          (onClick)="onBack()"
          pTooltip="Back to Products"
          tooltipPosition="bottom"
        />
        <h1 class="page-title">{{ productTitle() }}</h1>
        <!-- Mobile: Summary Popover Button -->
        <p-button
          class="mobile-summary-btn"
          icon="pi pi-info-circle"
          [text]="true"
          [rounded]="true"
          severity="secondary"
          (onClick)="summaryPopover.toggle($event)"
          pTooltip="Product Summary"
          tooltipPosition="bottom"
        />
      </div>
      <div class="header-right">
        <p-tag
          [value]="getStatusText()"
          [severity]="getStatusSeverity()"
          [rounded]="true"
        />
      </div>
    </div>

    <!-- Mobile Summary Popover -->
    <p-popover #summaryPopover styleClass="summary-popover">
      <div class="popover-summary">
        @if (productImageUrl()) {
          <img
            [src]="productImageUrl()!"
            [alt]="productTitle()"
            class="popover-image"
          />
        }
        <div class="popover-info">
          <div class="popover-row">
            <span class="popover-label">SKU</span>
            <span class="popover-value">{{ product()?.Sku || "—" }}</span>
          </div>
          <div class="popover-row">
            <span class="popover-label">QPilot ID</span>
            <span class="popover-value">{{ product()?.Id || "—" }}</span>
          </div>
          <div class="popover-row">
            <span class="popover-label">External ID</span>
            <span class="popover-value">{{
              product()?.ExternalId || "—"
            }}</span>
          </div>
          <div class="popover-row">
            <span class="popover-label">Type</span>
            <span class="popover-value">{{ product()?.Type || "—" }}</span>
          </div>
          <div class="popover-row">
            <span class="popover-label">Price</span>
            <span class="popover-value">{{
              formatPrice(product()?.Price)
            }}</span>
          </div>
          @if (product()?.SalePrice) {
            <div class="popover-row">
              <span class="popover-label">Sale Price</span>
              <span class="popover-value sale">{{
                formatPrice(product()?.SalePrice)
              }}</span>
            </div>
          }
          <div class="popover-row">
            <span class="popover-label">Stock</span>
            <span class="popover-value">{{ getStockText() }}</span>
          </div>
          <div class="popover-row">
            <span class="popover-label">Synced</span>
            <span class="popover-value">{{
              formatShortDate(lastSyncedDate())
            }}</span>
          </div>
        </div>
      </div>
    </p-popover>

    <!-- Content Area -->
    <div class="page-content">
      <!-- Left Column: Product Summary Panel (Desktop Only) -->
      <div class="product-summary-panel">
        <div class="panel-header">
          <i class="pi pi-box"></i>
          <span>Product Summary</span>
        </div>

        <div class="panel-body">
          <!-- Product Image -->
          @if (productImageUrl()) {
            <img
              [ngSrc]="productImageUrl()!"
              [alt]="productTitle()"
              width="240"
              height="240"
              class="product-image"
              priority
              (error)="onImageError($event)"
            />
          } @else {
            <div class="product-image-placeholder">
              <i class="pi pi-box"></i>
            </div>
          }

          <!-- Summary Info -->
          <div class="summary-info">
            @if (product()?.Sku) {
              <div class="summary-row highlight">
                <span class="summary-label">SKU</span>
                <span class="summary-value sku">{{ product()?.Sku }}</span>
              </div>
            }
            <div class="summary-row">
              <span class="summary-label">QPilot ID</span>
              <span class="summary-value">{{ product()?.Id || "—" }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">External ID</span>
              <span class="summary-value">{{
                product()?.ExternalId || "—"
              }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Type</span>
              <span class="summary-value">{{ product()?.Type || "—" }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Price</span>
              <span class="summary-value price">{{
                formatPrice(product()?.Price)
              }}</span>
            </div>
            @if (product()?.SalePrice) {
              <div class="summary-row">
                <span class="summary-label">Sale Price</span>
                <span class="summary-value sale">{{
                  formatPrice(product()?.SalePrice)
                }}</span>
              </div>
            }
            <div class="summary-row">
              <span class="summary-label">Stock</span>
              <p-tag
                [value]="getStockText()"
                [severity]="getStockSeverity()"
                [rounded]="true"
                styleClass="stock-tag"
              />
            </div>
            <div class="summary-row">
              <span class="summary-label">Synced</span>
              <span class="summary-value">{{
                formatShortDate(lastSyncedDate())
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Tabbed Content (Desktop) / Accordion (Mobile) -->
      <div class="product-details-area">
        <!-- Desktop: Tabs -->
        <div class="desktop-tabs">
          <p-tabView [scrollable]="true" styleClass="product-detail-tabs">
            <!-- Basic Info Tab -->
            <p-tabPanel header="Basic Info">
              <div class="tab-content">
                <!-- Product Identity -->
                <div class="section-header">
                  <i class="pi pi-tag"></i>
                  <span>Product Identity</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card">
                    <span class="mini-card-label">Product Name</span>
                    <span class="mini-card-value">{{
                      product()?.Title || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">SKU</span>
                    <span class="mini-card-value mono">{{
                      product()?.Sku || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Product Type</span>
                    <span class="mini-card-value">{{
                      product()?.Type || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Parent Product ID</span>
                    <span class="mini-card-value">{{
                      product()?.ParentProductId || "N/A"
                    }}</span>
                  </div>
                </div>

                <!-- Platform IDs -->
                <div class="section-header">
                  <i class="pi pi-link"></i>
                  <span>Platform IDs</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card">
                    <span class="mini-card-label">QPilot ID</span>
                    <span class="mini-card-value">{{
                      product()?.Id || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">External ID</span>
                    <span class="mini-card-value">{{
                      product()?.ExternalId || "N/A"
                    }}</span>
                    <span class="mini-card-hint"
                      >Read-only. Managed by your platform.</span
                    >
                  </div>
                </div>

                <!-- Description -->
                @if (product()?.Description) {
                  <div class="section-header">
                    <i class="pi pi-align-left"></i>
                    <span>Description</span>
                  </div>
                  <div class="description-box">
                    {{ product()?.Description }}
                  </div>
                }
              </div>
            </p-tabPanel>

            <!-- Pricing Tab -->
            <p-tabPanel header="Pricing">
              <div class="tab-content">
                <div class="section-header">
                  <i class="pi pi-dollar"></i>
                  <span>Pricing Information</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card accent">
                    <span class="mini-card-label">Regular Price</span>
                    <span class="mini-card-value large">{{
                      formatPrice(product()?.Price)
                    }}</span>
                  </div>
                  <div class="mini-card" [class.sale]="product()?.SalePrice">
                    <span class="mini-card-label">Sale Price</span>
                    <span class="mini-card-value large">{{
                      product()?.SalePrice
                        ? formatPrice(product()?.SalePrice)
                        : "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Tax Class</span>
                    <span class="mini-card-value">{{
                      product()?.TaxClass || "N/A"
                    }}</span>
                  </div>
                </div>
              </div>
            </p-tabPanel>

            <!-- Inventory Tab -->
            <p-tabPanel header="Inventory">
              <div class="tab-content">
                <div class="section-header">
                  <i class="pi pi-box"></i>
                  <span>Stock Information</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card">
                    <span class="mini-card-label">Stock Level</span>
                    <span class="mini-card-value">{{
                      getStockLevelDisplay()
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Backorders Allowed</span>
                    <span class="mini-card-value">{{
                      product()?.BackordersAllowed ? "Yes" : "No"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-check-circle"></i>
                  <span>Availability Settings</span>
                </div>
                <div class="availability-cards">
                  <div
                    class="availability-card"
                    [class.enabled]="addToScheduledOrderEnabled()"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="addToScheduledOrderEnabled()"
                        [class.pi-times]="!addToScheduledOrderEnabled()"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title"
                        >Add To Scheduled Order</span
                      >
                      <span class="availability-desc"
                        >Allow customers to find this product and add to their
                        scheduled orders.</span
                      >
                    </div>
                  </div>
                  <div
                    class="availability-card"
                    [class.enabled]="processScheduledOrderEnabled()"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="processScheduledOrderEnabled()"
                        [class.pi-times]="!processScheduledOrderEnabled()"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title"
                        >Process Scheduled Order</span
                      >
                      <span class="availability-desc"
                        >Include this product in future scheduled orders.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </p-tabPanel>

            <!-- Shipping Tab -->
            <p-tabPanel header="Shipping">
              <div class="tab-content">
                <div class="section-header">
                  <i class="pi pi-truck"></i>
                  <span>Shipping Class</span>
                </div>
                <div class="mini-cards-grid single">
                  <div class="mini-card">
                    <span class="mini-card-label">Class</span>
                    <span class="mini-card-value">{{
                      product()?.ShippingClass || "N/A"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-arrows-alt"></i>
                  <span>Dimensions</span>
                </div>
                <div class="mini-cards-grid cols-4">
                  <div class="mini-card">
                    <span class="mini-card-label">Length</span>
                    <span class="mini-card-value">{{
                      product()?.Length ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Width</span>
                    <span class="mini-card-value">{{
                      product()?.Width ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Height</span>
                    <span class="mini-card-value">{{
                      product()?.Height ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Unit</span>
                    <span class="mini-card-value">{{
                      product()?.LengthUnitType || "N/A"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-sort-amount-down"></i>
                  <span>Weight</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card">
                    <span class="mini-card-label">Weight</span>
                    <span class="mini-card-value">{{
                      product()?.Weight ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Unit</span>
                    <span class="mini-card-value">{{
                      product()?.WeightUnitType || "N/A"
                    }}</span>
                  </div>
                </div>
              </div>
            </p-tabPanel>

            <!-- Scheduling Tab -->
            <p-tabPanel header="Scheduling">
              <div class="tab-content">
                <div class="section-header">
                  <i class="pi pi-chart-bar"></i>
                  <span>Scheduled Orders Summary</span>
                </div>
                <div class="stats-row">
                  <div class="stat-mini-card active">
                    <i class="pi pi-play"></i>
                    <div class="stat-info">
                      <span class="stat-value">0</span>
                      <span class="stat-label">Active</span>
                    </div>
                  </div>
                  <div class="stat-mini-card paused">
                    <i class="pi pi-pause"></i>
                    <div class="stat-info">
                      <span class="stat-value">0</span>
                      <span class="stat-label">Paused</span>
                    </div>
                  </div>
                  <div class="stat-mini-card failed">
                    <i class="pi pi-exclamation-triangle"></i>
                    <div class="stat-info">
                      <span class="stat-value">0</span>
                      <span class="stat-label">Failed</span>
                    </div>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-cog"></i>
                  <span>Scheduling Settings</span>
                </div>
                <div class="mini-cards-grid">
                  <div class="mini-card">
                    <span class="mini-card-label">Quantity Label</span>
                    <span class="mini-card-value">{{
                      product()?.QuantityLabel || "N/A"
                    }}</span>
                  </div>
                </div>
                <div class="availability-cards" style="margin-top: 0.75rem">
                  <div
                    class="availability-card"
                    [class.enabled]="product()?.Active"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="product()?.Active"
                        [class.pi-times]="!product()?.Active"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title">Product Active</span>
                      <span class="availability-desc"
                        >Product is active for scheduled orders</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </p-tabPanel>
          </p-tabView>
        </div>

        <!-- Mobile: Accordion -->
        <div class="mobile-accordion">
          <p-accordion [multiple]="true" [activeIndex]="[0]">
            <!-- Basic Info -->
            <p-accordionTab header="Basic Info">
              <div class="accordion-content">
                <div class="section-header">
                  <i class="pi pi-tag"></i>
                  <span>Product Identity</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Product Name</span>
                    <span class="mini-card-value">{{
                      product()?.Title || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">SKU</span>
                    <span class="mini-card-value mono">{{
                      product()?.Sku || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Product Type</span>
                    <span class="mini-card-value">{{
                      product()?.Type || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Parent Product ID</span>
                    <span class="mini-card-value">{{
                      product()?.ParentProductId || "N/A"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-link"></i>
                  <span>Platform IDs</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">QPilot ID</span>
                    <span class="mini-card-value">{{
                      product()?.Id || "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">External ID</span>
                    <span class="mini-card-value">{{
                      product()?.ExternalId || "N/A"
                    }}</span>
                    <span class="mini-card-hint"
                      >Read-only. Managed by your platform.</span
                    >
                  </div>
                </div>

                @if (product()?.Description) {
                  <div class="section-header">
                    <i class="pi pi-align-left"></i>
                    <span>Description</span>
                  </div>
                  <div class="description-box">
                    {{ product()?.Description }}
                  </div>
                }
              </div>
            </p-accordionTab>

            <!-- Pricing -->
            <p-accordionTab header="Pricing">
              <div class="accordion-content">
                <div class="section-header">
                  <i class="pi pi-dollar"></i>
                  <span>Pricing Information</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card accent">
                    <span class="mini-card-label">Regular Price</span>
                    <span class="mini-card-value large">{{
                      formatPrice(product()?.Price)
                    }}</span>
                  </div>
                  <div class="mini-card" [class.sale]="product()?.SalePrice">
                    <span class="mini-card-label">Sale Price</span>
                    <span class="mini-card-value large">{{
                      product()?.SalePrice
                        ? formatPrice(product()?.SalePrice)
                        : "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Tax Class</span>
                    <span class="mini-card-value">{{
                      product()?.TaxClass || "N/A"
                    }}</span>
                  </div>
                </div>
              </div>
            </p-accordionTab>

            <!-- Inventory -->
            <p-accordionTab header="Inventory">
              <div class="accordion-content">
                <div class="section-header">
                  <i class="pi pi-box"></i>
                  <span>Stock Information</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Stock Level</span>
                    <span class="mini-card-value">{{
                      getStockLevelDisplay()
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Backorders Allowed</span>
                    <span class="mini-card-value">{{
                      product()?.BackordersAllowed ? "Yes" : "No"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-check-circle"></i>
                  <span>Availability Settings</span>
                </div>
                <div class="availability-cards">
                  <div
                    class="availability-card"
                    [class.enabled]="addToScheduledOrderEnabled()"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="addToScheduledOrderEnabled()"
                        [class.pi-times]="!addToScheduledOrderEnabled()"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title"
                        >Add To Scheduled Order</span
                      >
                      <span class="availability-desc"
                        >Allow customers to add to scheduled orders.</span
                      >
                    </div>
                  </div>
                  <div
                    class="availability-card"
                    [class.enabled]="processScheduledOrderEnabled()"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="processScheduledOrderEnabled()"
                        [class.pi-times]="!processScheduledOrderEnabled()"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title"
                        >Process Scheduled Order</span
                      >
                      <span class="availability-desc"
                        >Include in future scheduled orders.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </p-accordionTab>

            <!-- Shipping -->
            <p-accordionTab header="Shipping">
              <div class="accordion-content">
                <div class="section-header">
                  <i class="pi pi-truck"></i>
                  <span>Shipping Class</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Class</span>
                    <span class="mini-card-value">{{
                      product()?.ShippingClass || "N/A"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-arrows-alt"></i>
                  <span>Dimensions</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Length</span>
                    <span class="mini-card-value">{{
                      product()?.Length ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Width</span>
                    <span class="mini-card-value">{{
                      product()?.Width ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Height</span>
                    <span class="mini-card-value">{{
                      product()?.Height ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Unit</span>
                    <span class="mini-card-value">{{
                      product()?.LengthUnitType || "N/A"
                    }}</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-sort-amount-down"></i>
                  <span>Weight</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Weight</span>
                    <span class="mini-card-value">{{
                      product()?.Weight ?? "N/A"
                    }}</span>
                  </div>
                  <div class="mini-card">
                    <span class="mini-card-label">Unit</span>
                    <span class="mini-card-value">{{
                      product()?.WeightUnitType || "N/A"
                    }}</span>
                  </div>
                </div>
              </div>
            </p-accordionTab>

            <!-- Scheduling -->
            <p-accordionTab header="Scheduling">
              <div class="accordion-content">
                <div class="section-header">
                  <i class="pi pi-chart-bar"></i>
                  <span>Scheduled Orders Summary</span>
                </div>
                <div class="stats-row mobile">
                  <div class="stat-mini-card active">
                    <span class="stat-value">0</span>
                    <span class="stat-label">Active</span>
                  </div>
                  <div class="stat-mini-card paused">
                    <span class="stat-value">0</span>
                    <span class="stat-label">Paused</span>
                  </div>
                  <div class="stat-mini-card failed">
                    <span class="stat-value">0</span>
                    <span class="stat-label">Failed</span>
                  </div>
                </div>

                <div class="section-header">
                  <i class="pi pi-cog"></i>
                  <span>Scheduling Settings</span>
                </div>
                <div class="mini-cards-grid mobile">
                  <div class="mini-card">
                    <span class="mini-card-label">Quantity Label</span>
                    <span class="mini-card-value">{{
                      product()?.QuantityLabel || "N/A"
                    }}</span>
                  </div>
                </div>
                <div class="availability-cards" style="margin-top: 0.75rem">
                  <div
                    class="availability-card"
                    [class.enabled]="product()?.Active"
                  >
                    <div class="availability-icon">
                      <i
                        class="pi"
                        [class.pi-check]="product()?.Active"
                        [class.pi-times]="!product()?.Active"
                      ></i>
                    </div>
                    <div class="availability-content">
                      <span class="availability-title">Product Active</span>
                      <span class="availability-desc"
                        >Product is active for scheduled orders</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </p-accordionTab>
          </p-accordion>
        </div>
      </div>
    </div>
  }

  <!-- Error State -->
  @if (!isLoading() && !product()) {
    <div class="error-state">
      <div class="error-content">
        <i class="pi pi-box"></i>
        <h3>Product Not Found</h3>
        <p>The requested product could not be loaded.</p>
        <p-button
          label="Back to Products"
          icon="pi pi-arrow-left"
          [text]="true"
          (onClick)="onBack()"
        />
      </div>
    </div>
  }
</div>

<!-- Toast Messages -->
<p-toast position="top-right" />
